/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from 'react';
import { useConfigStore } from '@/stores';
import type { TFunction } from 'i18next';

export const useFnbBuildingOptions = (t: TFunction<'translation', undefined>) => {
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);
    const language = useConfigStore((state) => state.config.ui.language);

    return useMemo(() => {
        const allBuildingOption = {
            label: t('shop.search.text.content.all_building'),
            value: 'all',
        };

        const listBuildings =
            buildings?.map((building) => ({
                label: building.name[language] || building.name['ko'],
                value: building.id,
            })) || [];

        return buildings?.length && buildings.length > 1 ? [allBuildingOption, ...listBuildings] : listBuildings;
    }, [language, buildings]);
};

/**
 * Hook to memoize floor list computation with optimized logic
 */
export const useFnbFloorList = (selectedBuildingId: string | null, t: TFunction) => {
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);
    const language = useConfigStore((state) => state.config.ui.language);

    return useMemo(() => {
        const allFloorOption = {
            id: 'all',
            label: t('shop.search.text.content.all_floor'),
            value: 'all',
            data: null,
        };

        if (!buildings) {
            return [allFloorOption];
        }

        // === If "all buildings" is selected ===
        if (selectedBuildingId === 'all') {
            const labelToFloorCodesMap = new Map<string, Set<string>>();

            buildings.forEach((building) => {
                if (!building.filterPoiContents.fnb?.length) return;

                const floorCodesWithContent = new Set(building.filterPoiContents.fnb.map((poi) => poi.studioFloorId));

                building.floors?.forEach((floor) => {
                    if (!floorCodesWithContent.has(floor.floorCode)) return;

                    const label = floor.name[language] || floor.name['ko'];
                    if (!labelToFloorCodesMap.has(label)) {
                        labelToFloorCodesMap.set(label, new Set<string>());
                    }
                    labelToFloorCodesMap.get(label)!.add(floor.floorCode);
                });
            });

            const mergedFloors = Array.from(labelToFloorCodesMap.entries()).map(([label, codeSet]) => ({
                id: label,
                label,
                value: label,
                data: Array.from(codeSet),
            }));

            if (mergedFloors.length === 0) return [allFloorOption];

            // Sort floors efficiently based on label (supports B-levels)
            mergedFloors.sort((a, b) => {
                const aMatch = a.label.match(/(B?)(\d+)/i);
                const bMatch = b.label.match(/(B?)(\d+)/i);

                if (!aMatch || !bMatch) return a.label.localeCompare(b.label);

                const aNum = aMatch[1].toUpperCase() === 'B' ? -parseInt(aMatch[2], 10) : parseInt(aMatch[2], 10);
                const bNum = bMatch[1].toUpperCase() === 'B' ? -parseInt(bMatch[2], 10) : parseInt(bMatch[2], 10);

                return aNum - bNum;
            });

            return [allFloorOption, ...mergedFloors];
        }

        // === If a specific building is selected ===
        const buildingId = selectedBuildingId || buildings[0]?.id;
        if (!buildingId) return [allFloorOption];

        const building = buildings.find((b) => b.id === buildingId);
        if (!building || !building.filterPoiContents.fnb?.length) return [allFloorOption];

        const floorCodesWithContent = new Set(building.filterPoiContents.fnb.map((poi) => poi.studioFloorId));

        const floors =
            building.floors
                ?.filter((floor) => floorCodesWithContent.has(floor.floorCode))
                .map((floor) => ({
                    id: floor.id,
                    label: floor.name[language] || floor.name['ko'],
                    value: floor.id,
                    data: floor,
                }))
                .sort((a, b) => {
                    const aMatch = a.label.match(/(B?)(\d+)/i);
                    const bMatch = b.label.match(/(B?)(\d+)/i);

                    if (!aMatch || !bMatch) return a.label.localeCompare(b.label);

                    const aNum = aMatch[1].toUpperCase() === 'B' ? -parseInt(aMatch[2], 10) : parseInt(aMatch[2], 10);
                    const bNum = bMatch[1].toUpperCase() === 'B' ? -parseInt(bMatch[2], 10) : parseInt(bMatch[2], 10);

                    return aNum - bNum;
                }) || [];

        return floors.length > 0 ? [allFloorOption, ...floors] : [allFloorOption];
    }, [language, buildings, selectedBuildingId]);
};

export const useFnbCategories = (selectedBuildingId: string) => {
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);
    const mergedCategories = useConfigStore((state) => state.config.mergedCategories.fnb);

    if (selectedBuildingId === 'all') {
        return mergedCategories;
    }

    const building = buildings?.find((b) => b.id === selectedBuildingId);
    if (!building) return [];

    return building.filterCategories.fnb || [];
};
